import { useEffect, useMemo, useState } from "react";
import { readToken } from "./authState";

/**
 * 같은 수입자 과거 신고(2026-10-06) — 검토용 근거만, 자동 채택 없음.
 *
 * LCS 신고 화면에서 넘어올 때 주소 조각에 수입자 부호가 붙는다(`#sso=…&imp=<부호>` 또는 `#imp=<부호>`).
 * 조각은 서버로 전송되지 않아 접속 기록에 남지 않는다. 이 탭(sessionStorage)에만 기억하고, ✕ 로 지운다.
 * 서버(/api/onto/importer-history/evidence)는 로그인 사용자의 사무소 자료에서만 찾는다.
 */
const KEY = "loginhs:importer";

export function readImporter(storage = safeSession()) {
  try {
    const v = JSON.parse(storage?.getItem(KEY) || "null");
    return v && typeof v.code === "string" && v.code ? v : null;
  } catch (_e) {
    return null;
  }
}

export function setImporter(code, storage = safeSession()) {
  const c = String(code || "").trim().slice(0, 40);
  try {
    if (!c) storage?.removeItem(KEY);
    else storage?.setItem(KEY, JSON.stringify({ code: c }));
  } catch (_e) { /* 사생활 보호 모드 — 기능만 꺼진다 */ }
  notify();
}

/** 주소 조각의 imp= 를 꺼내 기억한다. sso= 가 함께 있으면 조각 지우기는 SSO 처리에 맡긴다. */
export function consumeImporterFragment({ location = window.location, history = window.history, storage = safeSession() } = {}) {
  try {
    const hash = location.hash || "";
    const m = hash.match(/[#&]imp=([^&]+)/);
    if (!m) return false;
    setImporter(decodeURIComponent(m[1]), storage);
    if (!/[#&]sso=/.test(hash)) {
      const rest = hash.replace(/^#/, "").split("&").filter((p) => p && !p.startsWith("imp=")).join("&");
      history.replaceState(null, "", location.pathname + location.search + (rest ? `#${rest}` : ""));
    }
    return true;
  } catch (_e) {
    return false;
  }
}

const listeners = new Set();
function notify() { for (const f of listeners) f(); }
function safeSession() { try { return window.sessionStorage; } catch (_e) { return null; } }

/** 현재 탭의 수입자 기준(바뀌면 다시 그린다). */
export function useImporter() {
  const [v, setV] = useState(() => readImporter());
  useEffect(() => {
    const f = () => setV(readImporter());
    listeners.add(f);
    return () => { listeners.delete(f); };
  }, []);
  return v;
}

const memo = new Map();
const IDLE = { status: "idle" };

/** { status: "idle"|"pending"|"ok"|"no_history"|"no_match"|"no_office"|"off"|"unavailable"|"limit", ... } */
/** ready: 결정례 힌트(모델 제안 포함)를 받은 뒤 — 모델 답과 견주려면 그것을 기다린다. */
export function useImporterEvidence(q, modelHsk, ready = true, fetchImpl = null) {
  const imp = useImporter();
  const text = String(q || "").replace(/\s+/g, " ").trim();
  const model = String(modelHsk || "").replace(/\D/g, "");
  const key = imp && ready ? `${imp.code}|${text}|${model}` : "";
  const [state, setState] = useState(() => (key && memo.has(key) ? memo.get(key) : { status: "idle" }));
  useEffect(() => {
    if (!key || !text) { setState({ status: "idle" }); return undefined; }
    if (memo.has(key)) { setState(memo.get(key)); return undefined; }
    const token = readToken();
    if (!token) { setState({ status: "idle" }); return undefined; }
    let alive = true;
    setState({ status: "pending" });
    (fetchImpl || fetch)("/api/onto/importer-history/evidence", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ importer_code: imp.code, text, model_hsk: model }),
    })
      .then((r) => (r && r.status === 402 ? { status: "limit" } : r && r.ok ? r.json() : { status: "unavailable" }))
      .catch(() => ({ status: "unavailable" }))
      .then((res) => {
        const v = res && typeof res === "object" ? res : { status: "unavailable" };
        if (v.status !== "unavailable") memo.set(key, v);
        if (alive) setState(v);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const code = imp ? imp.code : "";
  return useMemo(() => (code ? { ...(ready ? state : { status: "pending" }), importer: code } : IDLE), [code, ready, state]);
}

export function __resetImporterMemoForTest() {
  memo.clear();
}
